import {
  CapitalActionFormState,
  UpdateCapitalActionFormValues,
} from '../../hooks/useCapitalActionForm';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import CapitalContributionCallAllocations from './CapitalContributionCallAllocations';
import React, { useEffect } from 'react';
import { sumBy } from 'lodash';
import api from 'lib/trpcClient';

type Props = {
  vehicleId: string;
  index: number;
  baseCurrency: CurrencyCode;
};

const CapitalContributionCallDetails: React.FC<Props> = ({
  index,
  vehicleId,
  baseCurrency,
}) => {
  const {
    formMethods: { watch, setValue },
  } = useFormWithStateContext<
    UpdateCapitalActionFormValues,
    CapitalActionFormState
  >();

  const [baseAllocations] =
    api.capitalAction.getCommitmentsWithCapitalContributionBalances.useSuspenseQuery(
      {
        vehicleId,
      },
    );

  const capitalBreakdown = watch(`entries.${index}.capitalBreakdown`);

  useEffect(() => {
    if (!capitalBreakdown?.length) {
      const initialBreakdown = baseAllocations.map((a) => ({
        commitmentId: a.commitmentId,
        amountLcy: a.amountLcy,
        fundsCalled: {
          fromCommitmentLcy: a.fundsCalled.fromCommitmentLcy,
          fromRecallableDistributionLcy: 0,
        },
      }));
      setValue(`entries.${index}.capitalBreakdown`, initialBreakdown);
      setValue(
        `entries.${index}.totalAmountLcy`,
        sumBy(initialBreakdown, 'amountLcy'),
      );
    }
  }, [baseAllocations, capitalBreakdown?.length, index, setValue]);

  const handleAllocationsChange = (
    allocations: Array<{
      commitmentId: string;
      amountLcy: number;
      fundsCalled?: {
        fromCommitmentLcy: number;
        fromRecallableDistributionLcy: number;
      };
    }>,
  ) => {
    const breakdown = allocations.map((allocation) => ({
      commitmentId: allocation.commitmentId,
      amountLcy: allocation.amountLcy,
      fundsCalled: {
        fromCommitmentLcy: allocation.fundsCalled?.fromCommitmentLcy ?? 0,
        fromRecallableDistributionLcy:
          allocation.fundsCalled?.fromRecallableDistributionLcy ?? 0,
      },
    }));

    setValue(`entries.${index}.capitalBreakdown`, breakdown);
    setValue(`entries.${index}.totalAmountLcy`, sumBy(breakdown, 'amountLcy'));
  };

  return (
    <CapitalContributionCallAllocations
      title="Capital contribution"
      calledFundsLabel="Capital contribution called"
      currency={baseCurrency}
      baseAllocations={baseAllocations}
      onChange={handleAllocationsChange}
      capitalBreakdown={capitalBreakdown ?? []}
    />
  );
};

export default CapitalContributionCallDetails;
